import {
  CASH_COUNT_DENOMINATIONS,
  CASH_COUNT_MAX_QUANTITY,
  CASH_COUNT_STATION_NAME_MAX,
  emptyCashCountQuantities,
} from 'shared-models/browser';
import type { CashCountDenomination, CashCountDenominationKey, CashCountQuantities, PrinterView } from 'shared-models/browser';
import { getFixedPrinterId } from '../../hooks/fixed-printer.util';

export type CashCountInputs = Record<CashCountDenominationKey, string>;

export const denominationKey = (denomination: CashCountDenomination): CashCountDenominationKey => `${denomination}`;

// Stable id from the numeric key, never from display text (0.5 → "0-5").
export const denominationTestId = (denomination: CashCountDenomination): string =>
  `cash-count-qty-${String(denomination).replace('.', '-')}`;

export const sanitizeQuantityInput = (raw: string): string => raw.replace(/\D/g, '').slice(0, String(CASH_COUNT_MAX_QUANTITY).length);

export const inputsFromQuantities = (quantities: CashCountQuantities | null): CashCountInputs =>
  Object.fromEntries(
    CASH_COUNT_DENOMINATIONS.map((denomination) => {
      const quantity = quantities?.[denominationKey(denomination)] ?? 0;
      return [denominationKey(denomination), quantity > 0 ? String(quantity) : ''];
    }),
  ) as CashCountInputs;

export const quantitiesFromInputs = (inputs: CashCountInputs): CashCountQuantities => {
  const quantities = emptyCashCountQuantities();
  CASH_COUNT_DENOMINATIONS.forEach((denomination) => {
    const parsed = Number.parseInt(inputs[denominationKey(denomination)] || '0', 10);
    quantities[denominationKey(denomination)] = Number.isFinite(parsed) ? Math.min(parsed, CASH_COUNT_MAX_QUANTITY) : 0;
  });
  return quantities;
};

export const sameQuantities = (a: CashCountQuantities, b: CashCountQuantities): boolean =>
  CASH_COUNT_DENOMINATIONS.every((denomination) => a[denominationKey(denomination)] === b[denominationKey(denomination)]);

export const differenceLabel = (difference: number): string => {
  if (difference > 0) return 'עודף';
  if (difference < 0) return 'חוסר';
  return 'מאוזן';
};

// Device station name first, else the pinned fixed printer's name; null omits the receipt line.
export const resolveStationName = (tenantId: string, printers: PrinterView[]): string | null => {
  const stationary = window.localStorage.getItem(`posStationaryName:${tenantId}`)?.trim();
  if (stationary) return stationary.slice(0, CASH_COUNT_STATION_NAME_MAX);
  const fixedId = getFixedPrinterId();
  const fixed = fixedId == null ? null : printers.find((printer) => printer.id === fixedId);
  return fixed?.name ?? null;
};
